import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useT, SUPPORTED_LANGS } from "@/i18n/LanguageContext";
import { langFontFamily } from "@/i18n/langUtils";
import { useAuth } from "@/contexts/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import {
  Menu, X, ShieldAlert, Globe, Check, ChevronDown, LogOut, LayoutDashboard,
  User as UserIcon, TrendingUp, Database, Users, Calculator,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "/assets/logo.png";

const LanguageDropdown = () => {
  const { lang, setLang, t } = useT();
  const current = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 hover:border-[#D4AF37]/50 transition-colors"
          data-testid="lang-dropdown-trigger"
        >
          <Globe className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span className="hidden sm:inline">{current.native}</span>
          <span className="sm:hidden">{current.short}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#121212] border-white/10 text-white min-w-[180px]"
        data-testid="lang-dropdown-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">
          {t.lang?.choose || "Choose Language"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {SUPPORTED_LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => setLang(l.code)}
            className={`flex items-center justify-between cursor-pointer focus:bg-white/10 ${
              lang === l.code ? "text-[#D4AF37]" : "text-white/85"
            }`}
            data-testid={`lang-option-${l.code}`}
          >
            <span className="flex flex-col">
              <span className="text-sm font-medium" style={{ fontFamily: langFontFamily(l.code) }}>{l.native}</span>
              <span className="text-[10px] text-white/40 uppercase tracking-widest font-mono-alt">{l.label}</span>
            </span>
            {lang === l.code && <Check className="h-4 w-4 text-[#D4AF37]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const StockMarketMenu = ({ onNavigate, mobile = false }) => {
  const { t } = useT();
  const sm = t.nav?.stockMarket || "Stock Market";
  const items = [
    { to: "/risk-checker", label: t.nav.risk, icon: Calculator },
    { to: "/database", label: t.nav.database, icon: Database },
    { to: "/mentors", label: t.nav.mentors || "Mentors", icon: Users },
    { to: "/report", label: t.nav.reportTradingLoss || "Report trading loss", icon: TrendingUp },
  ];

  if (mobile) {
    return (
      <div className="py-1">
        <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">{sm}</div>
        {items.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/80 hover:bg-white/5"
          >
            <Icon className="h-4 w-4 text-[#D4AF37]" /> {label}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md text-white/75 hover:text-white transition-colors"
          data-testid="nav-stock-market"
        >
          <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
          {sm}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-[#121212] border-white/10 text-white min-w-[220px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">{sm}</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {items.map(({ to, label, icon: Icon }) => (
          <DropdownMenuItem key={to} asChild className="cursor-pointer focus:bg-white/10">
            <Link to={to} className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-[#D4AF37]" /> {label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AuthMenu = () => {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  if (!user) {
    return (
      <Link to="/login" className="inline-flex">
        <Button variant="outline" size="sm" className="border-white/20 hover:bg-white/5 text-white h-9 px-3" data-testid="header-login-btn">
          <UserIcon className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">{t.nav.signIn}</span>
        </Button>
      </Link>
    );
  }
  const initials = (user.name || user.email || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 pr-3 pl-1 py-1 hover:border-[#D4AF37]/50 transition-colors" data-testid="user-menu-trigger">
          <span className="h-7 w-7 rounded-full bg-[#D4AF37] text-black grid place-items-center text-xs font-bold overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}
          </span>
          <span className="hidden sm:inline text-xs font-medium text-white truncate max-w-[100px]">{user.name?.split(" ")[0] || "Account"}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white min-w-[200px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={() => nav("/dashboard")} className="cursor-pointer focus:bg-white/10" data-testid="menu-dashboard">
          <LayoutDashboard className="h-4 w-4 mr-2 text-[#D4AF37]" /> Dashboard
        </DropdownMenuItem>
        {(user.role === "admin" || user.role === "reviewer") && (
          <DropdownMenuItem onClick={() => nav("/admin")} className="cursor-pointer focus:bg-white/10" data-testid="menu-admin">
            <LayoutDashboard className="h-4 w-4 mr-2 text-[#FF6A60]" /> Staff panel
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} className="cursor-pointer focus:bg-white/10 text-[#FF6A60]" data-testid="menu-logout">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Header = () => {
  const { t, lang } = useT();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navHook = useNavigate();
  const closeMenu = () => setOpen(false);

  const mainLinks = [
    { to: "/", label: t.nav.home },
    { to: "/blog", label: "Blog" },
    { to: "/education", label: t.nav.education },
    { to: "/about", label: t.nav.about },
  ];

  const nativeLabel = (() => {
    if (lang === "hi") return "वित्तीय नुकसान सहायता";
    if (lang === "bn") return "আর্থিক ক্ষতি সহায়তা";
    if (lang === "mr") return "आर्थिक तोटा मदत";
    if (lang === "te") return "ఆర్థిక నష్టం మార్గదర్శనం";
    if (lang === "ta") return "நிதி இழப்பு வழிகாட்டுதல்";
    if (lang === "kn") return "ಆರ್ಥಿಕ ನಷ್ಟ ಮಾರ್ಗದರ್ಶನ";
    if (lang === "ml") return "സാമ്പത്തിക നഷ്ട മാർഗദർശനം";
    if (lang === "pa") return "ਵਿੱਤੀ ਨੁਕਸਾਨ ਮਾਰਗਦਰਸ਼ਨ";
    return "Financial Loss Guidance";
  })();

  return (
    <header className="glass sticky top-0 z-50 border-b border-white/10" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="logo-link">
          <div className="relative h-10 w-10 overflow-hidden rounded-lg ring-1 ring-white/10 group-hover:ring-[#D4AF37]/60 transition-all">
            <img src={LOGO_URL} alt="CHINTAMAT" className="h-full w-full object-cover" />
          </div>
          <div className="leading-tight hidden xs:block">
            <div className="font-display text-base font-extrabold tracking-tight">
              <span className="text-white">CHINTA</span>
              <span className="text-[#D4AF37]">MAT</span>
            </div>
            <div className="text-[10px] text-white/50 tracking-[0.18em] uppercase font-mono-alt hidden sm:block">{nativeLabel}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          {mainLinks.slice(0, 1).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => `relative px-3 py-2 text-sm rounded-md transition-colors ${isActive ? "text-[#D4AF37]" : "text-white/75 hover:text-white"}`}>
              {l.label}
            </NavLink>
          ))}
          <StockMarketMenu />
          {mainLinks.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => `relative px-3 py-2 text-sm rounded-md transition-colors ${isActive ? "text-[#D4AF37]" : "text-white/75 hover:text-white"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {(user?.role === "admin" || user?.role === "reviewer") && (
            <Link to="/admin" className="hidden sm:inline-flex">
              <Button variant="outline" size="sm" className="border-[#FF6A60]/40 text-[#FF6A60] hover:bg-[#FF6A60]/10 h-9 px-3 font-semibold" data-testid="header-admin-btn">
                Staff
              </Button>
            </Link>
          )}
          <NotificationsBell />
          <LanguageDropdown />
          <AuthMenu />
          <Link to="/#loss-categories" className="hidden sm:inline-flex">
            <Button className="bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold gap-2 text-sm" data-testid="header-report-btn">
              <ShieldAlert className="h-4 w-4" />
              {t.nav.reportCta}
            </Button>
          </Link>
          <button className="lg:hidden text-white p-1" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="mx-auto max-w-7xl px-4 py-3 flex flex-col gap-1">
            {mainLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={closeMenu}
                className={`px-3 py-3 rounded-md text-sm ${loc.pathname === l.to ? "text-[#D4AF37] bg-white/5" : "text-white/80"}`}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}>
                {l.label}
              </Link>
            ))}
            <StockMarketMenu onNavigate={closeMenu} mobile />
            <Link to="/#loss-categories" onClick={closeMenu}>
              <Button className="w-full mt-2 bg-[#FF3B30] hover:bg-[#cc2e25]" data-testid="mobile-report-btn">
                <ShieldAlert className="h-4 w-4 mr-2" /> {t.nav.reportCta}
              </Button>
            </Link>

            <div className="mt-3 pt-3 border-t border-white/10">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-md text-sm text-white/85 hover:bg-white/5" data-testid="mobile-dashboard-link">
                    <LayoutDashboard className="h-4 w-4 text-[#D4AF37]" />
                    {t.nav.dashboard || "Dashboard"}
                  </Link>
                  {(user.role === "admin" || user.role === "reviewer") && (
                    <Link to="/admin" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-md text-sm text-[#FF6A60] hover:bg-white/5" data-testid="mobile-admin-link">
                      Staff panel
                    </Link>
                  )}
                  <button onClick={async () => { closeMenu(); await logout(); navHook("/"); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm text-[#FF6A60] hover:bg-white/5" data-testid="mobile-logout-btn">
                    <LogOut className="h-4 w-4" /> {t.nav.signOut || "Sign out"}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" onClick={closeMenu}>
                    <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 gap-2" data-testid="mobile-login-btn">
                      <UserIcon className="h-4 w-4" /> {t.nav.signIn || "Sign in"}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={closeMenu}>
                    <Button className="w-full bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="mobile-register-btn">
                      Create account
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
